/**
 * WHEN a service-worker update is applied, and how a stale bundle recovers.
 *
 * ⚠️ TEMPORARY FORK of `@intrasync/web-core`'s `src/pwa/updatePolicy.ts`
 * (also exported from the package root). HQ does not depend on
 * `@intrasync/web-core` — only on react/stores/utils — so it cannot consume
 * the canonical copy until it adopts the host skeleton. Delete this file and
 * import from `@intrasync/web-core` the day that lands; keep the two in step
 * until then.
 *
 * Deliberately free of any `virtual:pwa-register` import so it can be unit
 * tested — that virtual module only exists inside a Vite build, so anything
 * importing it cannot be loaded by vitest. `pwaUtils.ts` owns the wiring;
 * this file owns the policy.
 */

export type UpdateSW = (reloadPage?: boolean) => Promise<void>;

/**
 * Applies a pending service-worker update the next time the tab is HIDDEN.
 *
 * Applying an update reloads the page. Doing that to a visible tab throws away
 * whatever the user was in the middle of — the failure this module exists to
 * prevent (see `build/pwaPreset.ts`). Waiting for `visibilitychange` means the
 * swap happens when they tab away or lock the screen, and they come back to a
 * fresh build having lost nothing.
 *
 * Fires at most once. Returns a disposer so callers and tests can detach.
 */
export function applyUpdateWhenHidden(
  updateSW: UpdateSW,
  doc: Pick<Document, "addEventListener" | "removeEventListener" | "visibilityState"> = document
): () => void {
  let done = false;

  const onVisibilityChange = () => {
    if (done || doc.visibilityState !== "hidden") return;
    done = true;
    doc.removeEventListener("visibilitychange", onVisibilityChange);
    void updateSW(true);
  };

  doc.addEventListener("visibilitychange", onVisibilityChange);
  return () => doc.removeEventListener("visibilitychange", onVisibilityChange);
}

const PRELOAD_RELOAD_FLAG = "intrasync:preload-reload";

/**
 * Recovers from a chunk that no longer exists on the server.
 *
 * Vite emits `vite:preloadError` when a lazily-imported chunk fails to load.
 * After a deploy, a tab still running the previous build asks for
 * content-hashed filenames that are gone — every lazy route then fails and the
 * app appears frozen on whatever screen it was on. Nothing in the fleet handled
 * this event before.
 *
 * One reload is the only recovery: the page has to re-fetch `index.html` to
 * learn the new hashes. Guarded by a session flag so a genuinely broken build
 * cannot produce a reload loop.
 *
 * Fires at most once. Returns a disposer so callers and tests can detach.
 */
export function installPreloadErrorRecovery(
  win: Pick<
    Window,
    "addEventListener" | "removeEventListener" | "location" | "sessionStorage"
  > = window
): () => void {
  let done = false;

  const onPreloadError = (event: Event) => {
    if (done) return;
    done = true;
    win.removeEventListener("vite:preloadError", onPreloadError);

    let alreadyReloaded = false;
    try {
      alreadyReloaded = win.sessionStorage.getItem(PRELOAD_RELOAD_FLAG) === "1";
      if (!alreadyReloaded) win.sessionStorage.setItem(PRELOAD_RELOAD_FLAG, "1");
    } catch {
      // Private mode / storage disabled — fall through and reload once.
    }

    // Already reloaded once this session and STILL failing: reloading again
    // would spin forever. Let the error surface instead.
    if (alreadyReloaded) return;

    event.preventDefault?.();
    win.location.reload();
  };

  win.addEventListener("vite:preloadError", onPreloadError);
  return () => win.removeEventListener("vite:preloadError", onPreloadError);
}

/** Clears the one-reload guard. Called once the app has booted far enough. */
export function clearPreloadErrorGuard(win: Pick<Window, "sessionStorage"> = window): void {
  try {
    win.sessionStorage.removeItem(PRELOAD_RELOAD_FLAG);
  } catch {
    // storage unavailable — nothing to clear
  }
}
